import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, DatePicker, Form, Input, Modal, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { errorText } from './helpers';

const lastMonthEnd = () => dayjs().subtract(1, 'month').endOf('month');

/** A new request: the accounts and the date say who is on the list. */
export default function CreateRequestModal({ open, companyId, onClose, onCreated }) {
  const { t } = useTranslation('partnerRequests');
  const [form] = Form.useForm();
  const [asOf, setAsOf] = useState(lastMonthEnd);

  useEffect(() => {
    if (open) {
      const date = lastMonthEnd();
      setAsOf(date);
      form.setFieldsValue({
        asOf: date,
        name: t('create.defaultName', { date: date.format('DD.MM.YYYY') }),
        accounts: [],
      });
    }
  }, [open, form, t]);

  const accounts = useQuery({
    queryKey: ['partnerRequests', companyId, 'accounts', asOf?.format('YYYY-MM-DD')],
    queryFn: () => API.partnerRequests.getAccounts(companyId, asOf.format('YYYY-MM-DD')),
    enabled: open && Boolean(asOf),
  });

  const create = useMutation({
    mutationFn: (values) =>
      API.partnerRequests.createRequest(companyId, {
        ...values,
        asOf: values.asOf.format('YYYY-MM-DD'),
      }),
    onSuccess: (created) => {
      onCreated(created);
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      maskClosable={false}
      title={t('create.title')}
      footer={
        <Space>
          <Button shape="round" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button shape="round" type="primary" loading={create.isPending} onClick={() => form.submit()}>
            {t('create.submit')}
          </Button>
        </Space>
      }
    >
      <p className="mb-3 text-sm opacity-70">{t('create.help')}</p>
      {create.isError && <Alert className="mb-3" type="error" showIcon message={errorText(t, create.error)} />}
      <Form form={form} layout="vertical" onFinish={(values) => create.mutate(values)}>
        <Form.Item name="name" label={t('create.name')} rules={[{ required: true, message: t('required') }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="asOf" label={t('create.asOf')} rules={[{ required: true, message: t('required') }]}>
          <DatePicker format="DD.MM.YYYY" className="w-full" allowClear={false} onChange={setAsOf} />
        </Form.Item>
        <Form.Item
          name="accounts"
          label={t('create.accounts')}
          extra={t('create.accountsHelp')}
          rules={[{ required: true, message: t('required') }]}
        >
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            loading={accounts.isLoading}
            options={(accounts.data || []).map((a) => ({
              value: a.account,
              label: t('create.accountOption', { account: a.account, name: a.name, count: a.partners }),
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
